import { useEffect, useMemo, useState } from 'react';
import { useApp, useMoney } from './store.tsx';

/**
 * The selected period, divided into bars you can tap.
 *
 * This replaced a fixed six-month chart that sat under a period picker and
 * never moved when the picker did — so the one interactive control on the
 * screen appeared to govern nothing.
 *
 * Built with real elements rather than an SVG so each bar is a button: on a
 * phone there is no hover, so `<title>` tooltips — which is how the other
 * charts here explain themselves — are unreachable. A bar has to be tappable
 * or its value cannot be read at all.
 */

export type Granularity = 'hour' | 'day' | 'week' | 'month';

export interface BreakdownBucket {
  key: string;
  startsAt: string;
  spentMinor: number;
  incomeMinor: number;
  count: number;
}

/**
 * Read a bucket in the account's timezone rather than the phone's.
 *
 * The server bucketed by the account's clock, so labelling by the browser's
 * would put a boundary at 00:00 under a label reading 20:00 for anyone whose
 * device disagrees with their account — a traveller, or a phone left on
 * another zone. Shifting the instant and then formatting in UTC makes the
 * label describe the bucket it sits under.
 */
function inAccountZone(iso: string, tzOffsetMinutes: number): Date {
  return new Date(new Date(iso).getTime() + tzOffsetMinutes * 60_000);
}

const UTC = { timeZone: 'UTC' } as const;

/** Axis label for a bucket: short, and only where it will not collide. */
function tickLabel(date: Date, g: Granularity, locale: string): string {
  const l = locale === 'ar' ? 'ar' : 'en-GB';
  if (g === 'hour') return String(date.getUTCHours()).padStart(2, '0');
  if (g === 'day') return date.toLocaleDateString(l, { weekday: 'narrow', ...UTC });
  if (g === 'week') return date.toLocaleDateString(l, { day: 'numeric', month: 'short', ...UTC });
  return date.toLocaleDateString(l, { month: 'short', ...UTC });
}

/** The full description, shown when a bar is selected. */
function fullLabel(date: Date, g: Granularity, locale: string): string {
  const l = locale === 'ar' ? 'ar' : 'en-GB';
  if (g === 'hour') {
    const end = (date.getUTCHours() + 1) % 24;
    return `${String(date.getUTCHours()).padStart(2, '0')}:00 – ${String(end).padStart(2, '0')}:00`;
  }
  if (g === 'day') return date.toLocaleDateString(l, { weekday: 'long', day: 'numeric', month: 'short', ...UTC });
  if (g === 'week') {
    const end = new Date(date.getTime() + 6 * 86_400_000);
    return `${date.toLocaleDateString(l, { day: 'numeric', month: 'short', ...UTC })} – ${end.toLocaleDateString(l, { day: 'numeric', month: 'short', ...UTC })}`;
  }
  return date.toLocaleDateString(l, { month: 'long', year: 'numeric', ...UTC });
}

/**
 * How many labels the axis can carry without them running together. Hourly
 * buckets get every sixth; anything coarser is sparse enough to label fully.
 */
/**
 * Rounds a value up to the next 1, 2, 2.5 or 5 × a power of ten.
 *
 * An axis topped by the largest bar's exact value labels the data with itself.
 * These steps are the ones that divide in half cleanly, which matters because
 * the midpoint gridline is drawn at half the top.
 */
function niceCeiling(value: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = [1, 2, 2.5, 5, 10].find((s) => normalized <= s) ?? 10;
  return step * magnitude;
}

function labelEvery(g: Granularity, count: number): number {
  if (g === 'hour') return 6;
  // Budgeted by how wide the label is, not by how many there are. "18 May" is
  // six characters where a day is one narrow letter, so a month of weeks fits
  // about six labels across a phone and fourteen of them ran together into an
  // unreadable strip. The full description of any bucket is one tap away in
  // the header, so a sparse axis loses nothing.
  const mostLabels = g === 'week' ? 6 : g === 'month' ? 8 : 16;
  return count > mostLabels ? Math.ceil(count / mostLabels) : 1;
}

export function BreakdownChart({ buckets, granularity, currency }: {
  buckets: BreakdownBucket[];
  granularity: Granularity;
  currency: string;
}): JSX.Element | null {
  const { locale, t, user } = useApp();
  const money = useMoney();
  const tz = user?.tzOffsetMinutes ?? 0;
  const [selected, setSelected] = useState<string | null>(null);

  // A new period is a new set of bars; a selection carried across would point
  // at a bucket that no longer exists.
  useEffect(() => { setSelected(null); }, [granularity, buckets.length]);

  /**
   * The top of the axis.
   *
   * Scaled on spending alone. It used to take the larger of spending and
   * income, and income is never drawn — so on any account where a salary
   * lands in one bucket, every bar was measured against a number four or five
   * times taller than anything on screen. The chart was mostly empty and the
   * variation between weeks was squashed into the bottom fifth of it.
   *
   * Rounded up to a round figure so the axis reads 4,000 rather than 3,907.41,
   * which is the tallest bar's value and tells you nothing you cannot already
   * see.
   */
  const max = useMemo(
    () => niceCeiling(Math.max(...buckets.map((b) => b.spentMinor), 1)),
    [buckets],
  );

  /** Gridlines: the top and the middle. A third line crowds 148px. */
  const ticks = useMemo(() => [max, max / 2], [max]);

  const total = useMemo(() => buckets.reduce((s, b) => s + b.spentMinor, 0), [buckets]);
  if (!buckets.length) return null;

  const active = buckets.find((b) => b.key === selected) ?? null;
  const every = labelEvery(granularity, buckets.length);
  const busiest = buckets.reduce((a, b) => (b.spentMinor > a.spentMinor ? b : a), buckets[0]!);

  // With nothing selected the header describes the period as a whole, and
  // points at its busiest bar — which is the thing worth noticing.
  const headline = active
    ? { label: fullLabel(inAccountZone(active.startsAt, tz), granularity, locale), amount: active.spentMinor, count: active.count }
    : { label: total > 0 ? fullLabel(inAccountZone(busiest.startsAt, tz), granularity, locale) : '—', amount: busiest.spentMinor, count: busiest.count };

  return (
    <div>
      <div className="row row--between" style={{ gap: 12, marginBottom: 12, minHeight: 40 }}>
        <span className="grow" style={{ minWidth: 0 }}>
          <span className="tiny muted" style={{ display: 'block' }}>
            {active
              ? headline.label
              : (total > 0
                  ? (locale === 'ar' ? `الأعلى · ${headline.label}` : `Busiest · ${headline.label}`)
                  : (locale === 'ar' ? 'لا إنفاق في هذه الفترة' : 'No spending in this period'))}
          </span>
          <span className="money strong" style={{ fontSize: 17 }}>
            {money(headline.amount, currency)}
          </span>
        </span>
        {headline.count > 0 && (
          <span className="tiny muted" style={{ flexShrink: 0 }}>
            {headline.count} {t('transactionsLower')}
          </span>
        )}
      </div>

      {/* The axis sits behind the bars rather than beside them: a gutter wide
          enough for "AED 4,000" costs a fifth of the width on a phone, and the
          bars are what the space is for. The lines stop short of the labels so
          nothing is drawn through the text. */}
      <div className="plot">
        <div className="plot__axis" aria-hidden>
          {ticks.map((v) => (
            <span key={v} className="plot__gridline" style={{ bottom: `${(v / max) * 100}%` }}>
              <span className="plot__tick">
                {money(v, currency, { compact: true, bare: true })}
              </span>
            </span>
          ))}
        </div>

      <div className="bars" role="group" aria-label={t('spendingBreakdown')}>
        {buckets.map((b, i) => {
          const date = inAccountZone(b.startsAt, tz);
          const height = max > 0 ? (b.spentMinor / max) * 100 : 0;
          const isActive = b.key === selected;
          const label = `${fullLabel(date, granularity, locale)}: ${money(b.spentMinor, currency)}`;
          return (
            <button
              key={b.key}
              type="button"
              className={`bars__col${isActive ? ' bars__col--active' : ''}`}
              aria-label={label}
              aria-pressed={isActive}
              onClick={() => setSelected(isActive ? null : b.key)}
            >
              <span className="bars__track">
                <span
                  className="bars__fill"
                  style={{
                    // Always at least a hairline, so an empty bucket still reads
                    // as a bucket rather than as a gap in the axis.
                    height: `${Math.max(height, b.spentMinor > 0 ? 3 : 1.5)}%`,
                    opacity: b.spentMinor > 0 ? 1 : 0.35,
                  }}
                />
              </span>
              <span className="bars__tick">
                {i % every === 0 ? tickLabel(date, granularity, locale) : ' '}
              </span>
            </button>
          );
        })}
      </div>
      </div>
    </div>
  );
}
